import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Impact,
  ImpactSummary,
  useBoardLabelCatalog,
  useBoardLabelIds,
  useSetBoardLabels,
} from '@/entities/collab';
import { FormError } from '@/shared/forms';
import { Button, Combobox, IconButton, Icons } from '@/shared/ui';

import styles from './BoardAccess.module.css';

/**
 * Board labels of this board as compact chips: add from the workspace catalog, remove, and put
 * them in the order they are shown (order is presentation only and never changes access). A label
 * that grants access (an access rule refers to it) changes who can open the board: the impact is
 * shown first and the change needs the administrative permission (the server decides).
 */
export function LabelsTab({
  boardId,
  workspaceId,
  canEditLabels,
}: {
  boardId: string;
  workspaceId: string;
  canEditLabels: boolean;
}) {
  const { t } = useTranslation();
  const catalog = useBoardLabelCatalog(workspaceId);
  const current = useBoardLabelIds(boardId);
  const save = useSetBoardLabels(boardId);
  const [edited, setEdited] = useState<string[] | null>(null);
  const draft = edited ?? current.data ?? [];
  const [impact, setImpact] = useState<Impact | null>(null);

  const labels = catalog.data ?? [];
  const byId = new Map(labels.map((l) => [l.id, l]));
  const changed =
    current.data !== undefined &&
    (draft.length !== current.data.length || draft.some((id, index) => id !== current.data[index]));
  const edit = (next: string[]) => {
    setEdited(next);
    setImpact(null);
  };
  const move = (index: number, delta: number) => {
    const next = [...draft];
    const [item] = next.splice(index, 1);
    if (item === undefined) return;
    next.splice(index + delta, 0, item);
    edit(next);
  };
  const available = labels.filter((l) => !draft.includes(l.id));

  return (
    <div className={styles.body}>
      <p className={styles.sub}>{t('access.labelsHint')}</p>
      {labels.length === 0 && <p className={styles.sub}>{t('access.noLabels')}</p>}
      <ul className={styles.chipRow} aria-label={t('access.tabs.labels')}>
        {draft.map((id, index) => {
          const label = byId.get(id);
          if (!label) return null;
          return (
            <li key={id} className={styles.boardLabelChip} data-testid="board-label-chip">
              <span aria-hidden className={styles.dot} style={{ background: label.color }} />
              <span>{label.name}</span>
              {label.access_controlling && (
                <span className={styles.sub}>{t('access.grantsAccess')}</span>
              )}
              {canEditLabels && (
                <span className={styles.chipTools}>
                  <IconButton
                    label={t('access.labelEarlier', { name: label.name })}
                    icon={<Icons.chevronLeft />}
                    disabled={index === 0}
                    onClick={() => {
                      move(index, -1);
                    }}
                  />
                  <IconButton
                    label={t('access.labelLater', { name: label.name })}
                    icon={<Icons.chevronRight />}
                    disabled={index === draft.length - 1}
                    onClick={() => {
                      move(index, 1);
                    }}
                  />
                  <IconButton
                    label={t('access.labelRemove', { name: label.name })}
                    icon={<Icons.close />}
                    onClick={() => {
                      edit(draft.filter((x) => x !== id));
                    }}
                  />
                </span>
              )}
            </li>
          );
        })}
        {draft.length === 0 && labels.length > 0 && (
          <li className={styles.sub}>{t('access.noBoardLabels')}</li>
        )}
      </ul>
      {canEditLabels && available.length > 0 && (
        <Combobox
          label={t('access.addLabel')}
          value={null}
          placeholder={t('access.addLabel')}
          searchPlaceholder={t('common.search')}
          emptyText={t('common.noResults')}
          size="sm"
          options={available.map((label) => ({
            value: label.id,
            label: label.name,
            leading: (
              <span aria-hidden className={styles.dot} style={{ background: label.color }} />
            ),
          }))}
          onChange={(id) => {
            edit([...draft, id]);
          }}
        />
      )}
      {impact && <ImpactSummary impact={impact} />}
      <FormError error={save.error} />
      {canEditLabels && (
        <div className={styles.actions}>
          <Button
            disabled={!changed || save.isPending}
            onClick={() => {
              save.mutate(
                { labelIds: draft, dryRun: true },
                {
                  onSuccess: (result) => {
                    setImpact(result.impact);
                  },
                },
              );
            }}
          >
            {t('access.previewImpact')}
          </Button>
          <Button
            variant="primary"
            disabled={!changed || save.isPending}
            onClick={() => {
              save.mutate(
                { labelIds: draft },
                {
                  onSuccess: () => {
                    setImpact(null);
                    setEdited(null);
                  },
                },
              );
            }}
          >
            {t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
